import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import { useParams } from 'react-router-dom';

import {

  Box,

  Typography,

  Card,

  CardContent,

  Table,

  TableBody,

  TableCell,

  TableContainer,

  TableHead,

  TableRow,

  Paper,

  Chip,

  CircularProgress,

  Alert,

  Grid,

  TextField,

  Button,

  Dialog,

  DialogTitle,

  DialogContent,

  DialogActions,

  Avatar,

  Divider,

  LinearProgress,

  TablePagination,

  IconButton,

  Tooltip,

  Tabs,

  Tab,

  Link,

  InputAdornment

} from '@mui/material';

import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Campaign as CampaignIcon,
  People as PeopleIcon,
  Photo as PhotoIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  Verified as VerifiedIcon,
  Autorenew as AutorenewIcon,
  Search as SearchIcon,
  Groups as GroupsIcon,
  OpenInNew as OpenInNewIcon,
  AutoStories as AutoStoriesIcon,
  Collections as CollectionsIcon
} from '@mui/icons-material';

import axios from 'axios';

import { openInstagramStorySmart } from '../utils/instagramStoryLinks';

function manualStoryRowIdToPathIdPublic(rowId: number): string {
  const n = BigInt(rowId);
  const mod = BigInt('10000000000000000000');
  const x = (n * BigInt(1103515245) + BigInt(12345)) % mod;
  return x.toString().padStart(19, '0');
}

function pathIdForInstagramStoryLinkPublic(story: {
  id: number;
  story_id?: string;
  story_type?: string;
  is_manual?: boolean | number;
}): string {
  const manual = story.story_type === 'manual' || Boolean(story.is_manual);
  if (manual) {
    return manualStoryRowIdToPathIdPublic(story.id);
  }
  const digits = String(story.story_id ?? '').replace(/\D/g, '');
  if (digits.length >= 19) return digits.slice(0, 19);
  if (digits.length >= 10) return digits.padStart(19, '0').slice(0, 19);
  if (digits.length > 0) return digits.padStart(19, '0');
  return manualStoryRowIdToPathIdPublic(story.id);
}

function buildInstagramStoryWebUrlPublic(
  username: string,
  story: { id: number; story_id?: string; story_type?: string; is_manual?: boolean | number }
): string {
  const u = username.replace(/^@/, '').trim();
  const pid = pathIdForInstagramStoryLinkPublic(story);
  return `https://www.instagram.com/stories/${encodeURIComponent(u)}/${pid}`;
}



interface Campaign {

  id: number;

  title: string;

  description: string;

  start_date: string;

  end_date: string;

  status: string;

  target_hashtags: string[];

  target_mentions: string[];

  target_links: any[];

  target_caption_keywords?: string[];

  content_tracking_mode?: 'story' | 'feed' | 'both';

  created_at: string;

  updated_at: string;

}



interface Influencer {

  id: number;

  instagram_username: string;

  full_name: string;

  follower_count: number;

  profile_picture: string;

  is_verified: boolean;

  story_count?: number;

  post_count?: number;

  has_shared: boolean;

  first_story_date: string | null;

  last_story_date: string | null;

  first_post_date?: string | null;

  last_post_date?: string | null;

}



interface Stats {

  total_influencers: number;

  total_followers?: number;

  influencers_shared: number;

  influencers_not_shared: number;

  total_stories: number;

  total_posts?: number;

  criteria?: {

    hashtags?: { key: string; story_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

    mentions?: { key: string; story_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

    links?: { key: string; story_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

  };

  criteria_posts?: {

    hashtags?: { key: string; post_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

    mentions?: { key: string; post_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

    links?: { key: string; post_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

    caption_keywords?: { key: string; post_count: number; influencer_count: number; first_date?: string | null; last_date?: string | null }[];

  };

}

interface ReportConfig {
  content_tracking_mode?: string;
  show_stories: boolean;
  show_posts: boolean;
}

interface AutoRefreshInfo {
  enabled: boolean;
  campaign_active: boolean;
  effective: boolean;
  interval_minutes: number;
  last_check_at: string | null;
  next_estimated_at: string | null;
  minutes_until_next: number | null;
  within_schedule_window: boolean;
  schedule_label: string;
  scan_running_now: boolean;
  server_now: string;
  status_key: string;
  message: string;
}



interface TabPanelProps {

  value: number;

  index: number;

  children: React.ReactNode;

}



const TabPanel: React.FC<TabPanelProps> = ({ value, index, children }) => (

  <div

    role="tabpanel"

    hidden={value !== index}

    id={`public-report-tabpanel-${index}`}

    aria-labelledby={`public-report-tab-${index}`}

  >

    {value === index && (

      <Box sx={{ pt: 3 }}>

        {children}

      </Box>

    )}

  </div>

);



const tabProps = (index: number) => ({

  id: `public-report-tab-${index}`,

  'aria-controls': `public-report-tabpanel-${index}`

});



const parseDetectedKeywords = (raw: any): string[] => {
  if (!raw) {
    return [];
  }

  if (Array.isArray(raw)) {
    return raw.filter((item) => typeof item === 'string' && item.trim().length > 0);
  }

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((item) => typeof item === 'string' && item.trim().length > 0);
      }
    } catch (error) {
      // JSON parse edilemezse fallback olarak virgülle ayrılmış kabul edelim
      return raw
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
  }

  return [];
};

/** DB/driver farklarına dayanıklı manuel satır tespiti (story_type veya is_manual) */
function isManualStoryRowPublic(story: { is_manual?: unknown; story_type?: string }): boolean {
  const im = story.is_manual;
  if (im === true || im === 1) return true;
  if (im != null && Number(im) === 1) return true;
  if (typeof im === 'string' && (im === '1' || im.toLowerCase() === 'true')) return true;
  if (story.story_type === 'manual') return true;
  return false;
}

/** Public raporda sayılan story: kriter tespitli VEYA manuel kayıt (sunucu ile aynı kural) */
function storyCountsTowardReportPublic(story: {
  detected_keywords?: unknown;
  keywordList?: string[];
  is_manual?: unknown;
  story_type?: string;
}): boolean {
  if (isManualStoryRowPublic(story)) return true;
  const kw = story.keywordList ?? parseDetectedKeywords(story?.detected_keywords);
  return kw.length > 0;
}

function reportCountedFromRawStory(s: {
  detected_keywords?: unknown;
  is_manual?: unknown;
  story_type?: string;
}): boolean {
  if (isManualStoryRowPublic(s)) return true;
  return parseDetectedKeywords(s.detected_keywords).length > 0;
}

/** Raporda saat dağılımı için Türkiye saati (yaz/kış otomatik) */
function getHourEuropeIstanbul(date: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Istanbul',
    hour: 'numeric',
    hour12: false
  }).formatToParts(date);
  const h = parts.find((p) => p.type === 'hour')?.value;
  return h != null ? parseInt(h, 10) : date.getHours();
}

const PublicReport: React.FC = () => {

  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [password, setPassword] = useState('');

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  
  
  const [campaign, setCampaign] = useState<Campaign | null>(null);

  const [influencers, setInfluencers] = useState<Influencer[]>([]);

  const [stats, setStats] = useState<Stats | null>(null);
 
  const [stories, setStories] = useState<any[]>([]);

  const [posts, setPosts] = useState<any[]>([]);

  const [reportConfig, setReportConfig] = useState<ReportConfig>({
    show_stories: true,
    show_posts: false
  });

  const [autoRefresh, setAutoRefresh] = useState<AutoRefreshInfo | null>(null);

  const [refreshClockTick, setRefreshClockTick] = useState(0);

  const [influencerPerformanceSearch, setInfluencerPerformanceSearch] = useState('');

  
  // Pagination

  const [storyPage, setStoryPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [postPage, setPostPage] = useState(0);

  const [postRowsPerPage, setPostRowsPerPage] = useState(50);

  const [activeTab, setActiveTab] = useState(0);

  const DAILY_SUMMARY_ROWS_PER_PAGE = 12;
  const [dailySummaryPage, setDailySummaryPage] = useState(0);

  
  
  // ZIP indirme progress

  const [zipDownloadDialog, setZipDownloadDialog] = useState(false);

  const [zipModalContext, setZipModalContext] = useState<'stories' | 'posts'>('stories');

  const [outerTab, setOuterTab] = useState<'story' | 'post'>('story');

  const [zipProgress, setZipProgress] = useState({ step: '', percent: 0 });


  const analysisRef = useRef<HTMLDivElement | null>(null);

  const getKeywordChipColor = (
    keyword: string
  ): 'default' | 'primary' | 'secondary' | 'info' | 'success' => {
    if (keyword.startsWith('#')) return 'primary';
    if (keyword.startsWith('@')) return 'secondary';
    if (/^https?:\/\//i.test(keyword.trim())) return 'info';
    return 'success';
  };

  const enrichedStories = useMemo(() => {
    return stories.map((story) => ({
      ...story,
      keywordList: parseDetectedKeywords(story?.detected_keywords)
    }));
  }, [stories]);

  const enrichedPosts = useMemo(() => {
    return posts.map((post) => ({
      ...post,
      keywordList: parseDetectedKeywords(post?.detected_keywords)
    }));
  }, [posts]);

  const enrichedPostsWithInfluencer = useMemo(() => {
    return enrichedPosts.map((post: any) => {
      const inf = influencers.find((i) => Number(i.id) === Number(post.influencer_id));
      const paths = Array.isArray(post.local_media_paths) ? post.local_media_paths : [];
      return {
        ...post,
        _avatar: inf?.profile_picture,
        _fullName: (inf?.full_name || '').trim(),
        _localPathCount: paths.length
      };
    });
  }, [enrichedPosts, influencers]);

  const detectedStories = useMemo(() => {
    return enrichedStories.filter((story) => story.keywordList.length > 0);
  }, [enrichedStories]);

  const reportCountedStories = useMemo(() => {
    return enrichedStories.filter((story) => storyCountsTowardReportPublic(story));
  }, [enrichedStories]);

  /** Katılım / paylaşmayan listesi: API'deki has_shared yerine gerçek rapor kümesi (manuel dahil) */
  const { influencerIdsWithReportStories, storyCountByInfluencerId } = useMemo(() => {
    const influencerIdsWithReportStories = new Set<number>();
    const storyCountByInfluencerId = new Map<number, number>();
    for (const s of reportCountedStories) {
      const raw = s.influencer_id;
      if (raw == null || raw === '') continue;
      const n = Number(raw);
      if (!Number.isFinite(n)) continue;
      influencerIdsWithReportStories.add(n);
      storyCountByInfluencerId.set(n, (storyCountByInfluencerId.get(n) || 0) + 1);
    }
    return { influencerIdsWithReportStories, storyCountByInfluencerId };
  }, [reportCountedStories]);

  const participationCounts = useMemo(() => {
    if (!influencers.length) {
      return { shared: 0, notShared: 0, total: 0 };
    }
    const shared = influencers.filter((i) => i.has_shared).length;
    return { shared, notShared: influencers.length - shared, total: influencers.length };
  }, [influencers]);

  const reportScope = useMemo<'story' | 'post'>(() => {
    const mode = reportConfig.content_tracking_mode || 'story';
    if (mode === 'both') return outerTab;
    if (mode === 'feed') return 'post';
    return 'story';
  }, [reportConfig.content_tracking_mode, outerTab]);

  const postCountByInfluencerId = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of enrichedPosts) {
      const id = Number((p as any).influencer_id);
      if (!Number.isFinite(id)) continue;
      m.set(id, (m.get(id) || 0) + 1);
    }
    return m;
  }, [enrichedPosts]);

  const participationScoped = useMemo(() => {
    if (!influencers.length) {
      return { shared: 0, notShared: 0, total: 0 };
    }
    if (reportScope === 'story') {
      const shared = influencers.filter((i) => influencerIdsWithReportStories.has(Number(i.id))).length;
      return { shared, notShared: influencers.length - shared, total: influencers.length };
    }
    const shared = influencers.filter((i) => (postCountByInfluencerId.get(Number(i.id)) ?? 0) > 0).length;
    return { shared, notShared: influencers.length - shared, total: influencers.length };
  }, [influencers, reportScope, influencerIdsWithReportStories, postCountByInfluencerId]);

  const postScopeOverviewKpis = useMemo(() => {
    const totalPosts = enrichedPosts.length;
    const reels = enrichedPosts.filter((p: any) => p.is_reel).length;
    const sharing = participationScoped.shared;
    const pct = participationScoped.total
      ? Math.round((participationScoped.shared / participationScoped.total) * 100)
      : 0;
    const avg = sharing > 0 ? totalPosts / sharing : 0;
    return { totalPosts, reels, pct, avg };
  }, [enrichedPosts, participationScoped]);

  /** Ardışık 2 saatlik pencerede en çok paylaşım (İstanbul saati) */
  const peakPostingHourRange = useMemo(() => {
    const counts = new Array(24).fill(0);
    let dated = 0;
    for (const s of reportCountedStories) {
      const ts = s?.story_timestamp ?? s?.detected_at;
      if (!ts) continue;
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) continue;
      const hour = getHourEuropeIstanbul(d);
      if (hour < 0 || hour > 23) continue;
      counts[hour] += 1;
      dated += 1;
    }
    if (dated === 0) return null;
    let bestStart = 0;
    let bestSum = -1;
    for (let h = 0; h <= 22; h++) {
      const sum = counts[h] + counts[h + 1];
      if (sum > bestSum) {
        bestSum = sum;
        bestStart = h;
      }
    }
    const fmt = (n: number) => `${String(n).padStart(2, '0')}:00`;
    return {
      label: `${fmt(bestStart)} – ${fmt(bestStart + 2)}`,
      countInWindow: bestSum,
      datedWithTimestamp: dated
    };
  }, [reportCountedStories]);

  const hasReportStories = reportCountedStories.length > 0;

  const storyDailySummary = useMemo(() => {
    const summaryMap = new Map<string, {
      date: string;
      dayName: string;
      storyCount: number;
      activeInfluencers: Set<string>;
      hashtagCount: number;
      mentionCount: number;
      linkCount: number;
    }>();

    reportCountedStories.forEach((story) => {
      const timestamp = story?.story_timestamp ? new Date(story.story_timestamp) : null;
      if (!timestamp) {
        return;
      }

      const dateKey = timestamp.toISOString().slice(0, 10);
      const existing = summaryMap.get(dateKey);
      const dayName = timestamp.toLocaleDateString('tr-TR', { weekday: 'long' });

      const hasHashtag = story.keywordList.some((kw: string) => kw.startsWith('#'));
      const hasMention = story.keywordList.some((kw: string) => kw.startsWith('@'));
      const hasLink = story.keywordList.some((kw: string) => kw.startsWith('http'));

      if (!existing) {
        summaryMap.set(dateKey, {
          date: dateKey,
          dayName,
          storyCount: 1,
          activeInfluencers: new Set(story.instagram_username ? [story.instagram_username] : []),
          hashtagCount: hasHashtag ? 1 : 0,
          mentionCount: hasMention ? 1 : 0,
          linkCount: hasLink ? 1 : 0
        });
      } else {
        existing.storyCount += 1;
        if (story.instagram_username) {
          existing.activeInfluencers.add(story.instagram_username);
        }
        if (hasHashtag) existing.hashtagCount += 1;
        if (hasMention) existing.mentionCount += 1;
        if (hasLink) existing.linkCount += 1;
      }
    });

    return Array.from(summaryMap.values())
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((item) => ({
        ...item,
        activeCount: item.activeInfluencers.size
      }));
  }, [reportCountedStories]);
 
  const keywordStats = useMemo(() => {
    if (!detectedStories.length) {
      return {
        totalKeywords: 0,
        avgPerStory: 0,
        uniqueKeywords: 0
      };
    }

    const totalKeywords = detectedStories.reduce((sum, story) => sum + story.keywordList.length, 0);
    const unique = new Set<string>();
    detectedStories.forEach((story) => {
      story.keywordList.forEach((kw: string) => unique.add(kw));
    });

    return {
      totalKeywords,
      avgPerStory: totalKeywords / detectedStories.length,
      uniqueKeywords: unique.size
    };
  }, [detectedStories]);

  const keywordPostStats = useMemo(() => {
    if (!enrichedPosts.length) {
      return {
        totalKeywords: 0,
        avgPerPost: 0,
        uniqueKeywords: 0
      };
    }

    const totalKeywords = enrichedPosts.reduce((sum, post) => sum + post.keywordList.length, 0);
    const unique = new Set<string>();
    enrichedPosts.forEach((post) => {
      post.keywordList.forEach((kw: string) => unique.add(kw));
    });

    return {
      totalKeywords,
      avgPerPost: totalKeywords / enrichedPosts.length,
      uniqueKeywords: unique.size
    };
  }, [enrichedPosts]);

  const storyTypeBreakdown = useMemo(() => {
     const counts = reportCountedStories.reduce(
       (acc, story) => {
         const type = story?.story_type;
         if (type === 'video') {
           acc.video += 1;
         } else if (type === 'image' || type === 'photo' || type === 'manual' || !type) {
           acc.image += 1;
         } else {
           acc.other += 1;
         }
         return acc;
       },
       { video: 0, image: 0, other: 0 }
     );

    const totalRelevant = counts.video + counts.image;
    const safeTotal = totalRelevant > 0 ? totalRelevant : 1;

    return {
      ...counts,
      total: totalRelevant,
      percentages: {
        video: (counts.video / safeTotal) * 100,
        image: (counts.image / safeTotal) * 100,
        other: totalRelevant > 0 ? 0 : 100
      }
    };
  }, [reportCountedStories]);

  const dailyStoryActivity = useMemo(() => {
    const activityMap = new Map<string, number>();

    reportCountedStories.forEach((story) => {
      const timestamp = story?.story_timestamp;
      if (!timestamp) return;
      const dateKey = new Date(timestamp).toISOString().slice(0, 10);
      activityMap.set(dateKey, (activityMap.get(dateKey) || 0) + 1);
    });

    return Array.from(activityMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [reportCountedStories]);

  const storyHighlights = useMemo(() => {
    if (!dailyStoryActivity.length || !reportCountedStories.length) {
      return null;
    }

    const totalStories = reportCountedStories.length;
    const sortedActivity = [...dailyStoryActivity].sort((a, b) => b.count - a.count);
    const mostActive = sortedActivity[0];
    const leastActive = sortedActivity[sortedActivity.length - 1];
    const avgPerDay = totalStories / dailyStoryActivity.length;

    const keywordFrequency = new Map<string, number>();
    detectedStories.forEach((story) => {
      story.keywordList.forEach((kw: string) => {
        keywordFrequency.set(kw, (keywordFrequency.get(kw) || 0) + 1);
      });
    });

    const topKeywords = Array.from(keywordFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([key, count]) => ({ key, count }));

    return {
      mostActive,
      leastActive,
      avgPerDay,
      topKeywords
    };
  }, [dailyStoryActivity, reportCountedStories, detectedStories]);

  const criteriaSummary = useMemo(() => {
    const buildSummary = (items?: { key: string; story_count: number; influencer_count: number }[]) => {
      const list = Array.isArray(items) ? items : [];
      const total = list.reduce((sum, item) => sum + (item?.story_count || 0), 0);
      const top = [...list]
        .sort((a, b) => (b?.story_count || 0) - (a?.story_count || 0))
        .slice(0, 5)
        .map((item) => ({
          key: item?.key || '-',
          story_count: item?.story_count || 0,
          influencer_count: item?.influencer_count || 0,
          ratio: total ? Math.round(((item?.story_count || 0) / total) * 100) : 0
        }));

      return {
        total,
        unique: list.length,
        top
      };
    };

    return {
      hashtags: buildSummary(stats?.criteria?.hashtags),
      mentions: buildSummary(stats?.criteria?.mentions),
      links: buildSummary(stats?.criteria?.links)
    };
  }, [stats]);

  const criteriaPostsSummary = useMemo(() => {
    const buildPostSummary = (items?: { key: string; post_count: number; influencer_count: number }[]) => {
      const list = Array.isArray(items) ? items : [];
      const total = list.reduce((sum, item) => sum + (item?.post_count || 0), 0);
      const top = [...list]
        .sort((a, b) => (b?.post_count || 0) - (a?.post_count || 0))
        .slice(0, 5)
        .map((item) => ({
          key: item?.key || '-',
          post_count: item?.post_count || 0,
          influencer_count: item?.influencer_count || 0,
          ratio: total ? Math.round(((item?.post_count || 0) / total) * 100) : 0
        }));
      return { total, unique: list.length, top };
    };

    return {
      hashtags: buildPostSummary(stats?.criteria_posts?.hashtags),
      mentions: buildPostSummary(stats?.criteria_posts?.mentions),
      links: buildPostSummary(stats?.criteria_posts?.links),
      captions: buildPostSummary(stats?.criteria_posts?.caption_keywords)
    };
  }, [stats]);

  const totalPostCriteriaMatches = useMemo(
    () =>
      criteriaPostsSummary.hashtags.total +
      criteriaPostsSummary.mentions.total +
      criteriaPostsSummary.links.total +
      criteriaPostsSummary.captions.total,
    [criteriaPostsSummary]
  );

  const topHashtag = criteriaSummary.hashtags.top[0];
  const topMention = criteriaSummary.mentions.top[0];
  const topLink = criteriaSummary.links.top[0];

  const influencerAnalytics = useMemo(() => {
    if (!influencers.length) {
      return {
        total: 0,
        averageStories: 0,
        medianStories: 0,
        shareRate: 0,
        verifiedShareRate: 0,
        topPerformers: [] as Influencer[]
      };
    }

    const total = influencers.length;
    const storyCounts = influencers
      .map((inf) => storyCountByInfluencerId.get(Number(inf.id)) ?? 0)
      .sort((a, b) => a - b);
    const storiesSum = storyCounts.reduce((sum, count) => sum + count, 0);
    const averageStories = storiesSum / total;
    const medianStories =
      total % 2 === 1
        ? storyCounts[Math.floor(total / 2)]
        : (storyCounts[total / 2 - 1] + storyCounts[total / 2]) / 2;

    const topPerformers = [...influencers]
      .sort((a, b) => (b.follower_count || 0) - (a.follower_count || 0))
      .slice(0, 5);

    const verified = influencers.filter((inf) => inf.is_verified);
    const verifiedShareRate = verified.length
      ? (verified.filter((inf) => inf.has_shared).length / verified.length) * 100
      : 0;

    const shareRate = participationCounts.total
      ? (participationCounts.shared / participationCounts.total) * 100
      : 0;

    return {
      total,
      averageStories,
      medianStories,
      shareRate,
      verifiedShareRate,
      topPerformers
    };
  }, [influencers, storyCountByInfluencerId, participationCounts]);

  /** Genel bakış / Kampanya Özeti: hikâye ve gönderi kapsamlarında paylaşan–paylaşmayan ile tutarlı takipçi kırılımı */
  const followerBreakdownScoped = useMemo(() => {
    if (!influencers.length) {
      return {
        total: 0,
        sharedSum: 0,
        notSharedSum: 0,
        avgPerInfluencer: 0,
        pctSharedOfTotal: 0,
        countWithData: 0
      };
    }
    const sumFollowers = (list: Influencer[]) =>
      list.reduce((s, i) => s + (Number(i.follower_count) || 0), 0);
    const total = sumFollowers(influencers);
    let sharedList: Influencer[];
    let notSharedList: Influencer[];
    if (reportScope === 'story') {
      sharedList = influencers.filter((i) => influencerIdsWithReportStories.has(Number(i.id)));
      notSharedList = influencers.filter((i) => !influencerIdsWithReportStories.has(Number(i.id)));
    } else {
      sharedList = influencers.filter((i) => (postCountByInfluencerId.get(Number(i.id)) ?? 0) > 0);
      notSharedList = influencers.filter((i) => (postCountByInfluencerId.get(Number(i.id)) ?? 0) === 0);
    }
    const sharedSum = sumFollowers(sharedList);
    const notSharedSum = sumFollowers(notSharedList);
    const avgPerInfluencer = total / influencers.length;
    const pctSharedOfTotal = total > 0 ? (sharedSum / total) * 100 : 0;
    const countWithData = influencers.filter(
      (i) => i.follower_count != null && Number(i.follower_count) > 0
    ).length;
    return {
      total,
      sharedSum,
      notSharedSum,
      avgPerInfluencer,
      pctSharedOfTotal,
      countWithData
    };
  }, [influencers, reportScope, influencerIdsWithReportStories, postCountByInfluencerId]);

  const totalCriteriaMatches = criteriaSummary.hashtags.total + criteriaSummary.mentions.total + criteriaSummary.links.total;

  const handleExportAnalysisPdf = async () => {
    if (!analysisRef.current) {
      return;
    }

    try {
      const canvas = await html2canvas(analysisRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      const safeTitle = (campaign?.title || 'kampanya-raporu')
        .replace(/[^a-zA-Z0-9-_çğıöşüÇĞİÖŞÜ ]/g, '')
        .trim()
        .replace(/\s+/g, '-');

      pdf.save(`${safeTitle || 'kampanya'}-kriter-analizi.pdf`);
    } catch (err) {
      console.error('PDF oluşturulamadı', err);
      alert('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };
 
 
  useEffect(() => {
    setDailySummaryPage(0);
  }, [storyDailySummary.length]);

  useEffect(() => {
    if (!autoRefresh?.next_estimated_at || autoRefresh.scan_running_now) return;
    const id = window.setInterval(() => setRefreshClockTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [autoRefresh?.next_estimated_at, autoRefresh?.scan_running_now]);

  const liveMinutesUntilRefresh =
    !autoRefresh?.next_estimated_at || autoRefresh.scan_running_now
      ? null
      : Math.max(
          0,
          Math.ceil(
            (new Date(autoRefresh.next_estimated_at).getTime() - Date.now()) / 60000
          )
        );
  void refreshClockTick;

  const filteredInfluencersForPerformance = useMemo(() => {
    const raw = influencerPerformanceSearch.trim().toLowerCase();
    if (!raw) return influencers;
    const q = raw.replace(/^@/, '');
    return influencers.filter((inf) => {
      const name = (inf.full_name || '').toLowerCase();
      const user = (inf.instagram_username || '').toLowerCase();
      return name.includes(q) || user.includes(q);
    });
  }, [influencers, influencerPerformanceSearch]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {

    setActiveTab(newValue);

  };



  const fetchReport = useCallback(async (pwd?: string) => {

    try {

      setLoading(true);

      setError('');

      

      // 1) Önce doğrulama yap (şifre varsa sessizce doğrula)

      try {

        await axios.post(`/api/public/report/${token}/verify`, pwd ? { password: pwd } : {});

      } catch (verifyErr: any) {

        // Şifre gerekliyse dialog aç ve GET çağrısı yapma


        if (verifyErr.response?.status === 401 && verifyErr.response?.data?.requiresPassword) {

          setPasswordDialogOpen(true);

          if (verifyErr.response?.data?.campaign) {

            setCampaign(verifyErr.response.data.campaign as any);

          }

          return; // erken çık - konsola 401 düşmeden durdurduk

        }

        // Diğer hatalar için normal akış (GET denenecek)

      }



      // 2) Doğrulama başarılı ise raporu çek
      
      const response = await axios.get(`/api/public/report/${token}`, {

        params: pwd ? { password: pwd } : {}

      });



      setCampaign(response.data.campaign);

      setAutoRefresh(response.data.auto_refresh ?? null);

      setStories(response.data.stories || []);

      setPosts(response.data.posts || []);

      setReportConfig(
        response.data.report_config || {
          show_stories: true,
          show_posts: false,
          content_tracking_mode: 'story'
        }
      );

      const infList = (response.data.influencers || []) as Influencer[];
      const storiesList = response.data.stories || [];
      const reportCounted = storiesList.filter((s: any) => reportCountedFromRawStory(s));
      const sharedInfluencerCount = infList.filter((i: Influencer) => i.has_shared).length;

      setInfluencers(infList);

      const fallbackTotalFollowers = infList.reduce(
        (sum: number, inf: Influencer) => sum + (Number(inf.follower_count) || 0),
        0
      );

      const rawStats =
        response.data.stats ||
        ({
          total_influencers: infList.length,
          influencers_shared: sharedInfluencerCount,
          influencers_not_shared: infList.length - sharedInfluencerCount,
          total_stories: reportCounted.length
        } as Stats);

      setStats({
        ...rawStats,
        total_followers: rawStats.total_followers ?? fallbackTotalFollowers,
        total_posts: rawStats.total_posts ?? (response.data.posts?.length ?? 0)
      });

      setPasswordDialogOpen(false);

    } catch (err: any) {

        setError(err.response?.data?.message || 'Rapor yüklenemedi');
        setAutoRefresh(null);

    } finally {

      setLoading(false);

    }

  }, [token]);


  useEffect(() => {

    if (token) {

      fetchReport();

    }

  }, [token, fetchReport]);

  useEffect(() => {
    setPostPage(0);
  }, [posts]);


  const handlePasswordSubmit = () => {

    if (password.trim()) {

      fetchReport(password);

    }

  };



  const handleChangePage = (event: unknown, newPage: number) => {

    setStoryPage(newPage);

  };



  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {

    setRowsPerPage(parseInt(event.target.value, 10));

    setStoryPage(0);

  };

  const handleChangePostPage = (_event: unknown, newPage: number) => {
    setPostPage(newPage);
  };

  const handleChangePostRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostRowsPerPage(parseInt(event.target.value, 10));
    setPostPage(0);
  };

  const handleDailySummaryPageChange = (_event: unknown, newPage: number) => {
    setDailySummaryPage(newPage);
  };

  const handleDownloadStory = async (storyId: number, story: any) => {

    try {

      // Public story download endpoint (token ile)

      const response = await axios.get(`/api/stories/public/${storyId}/download/${token}`, {

        responseType: 'blob'

      });



      // Dosya adını backend'den gelen response'dan al veya oluştur

      const contentDisposition = response.headers['content-disposition'];

      let fileName = `story-${storyId}.jpg`;

      
      
      if (contentDisposition) {

        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);

        if (fileNameMatch && fileNameMatch[1]) {

          fileName = fileNameMatch[1];

        }

      } else {

        // Fallback: Backend'den gelmediyse oluştur

        const date = new Date(story.story_timestamp).toISOString().split('T')[0];

        const fileExtension = story.story_type === 'video' ? 'mp4' : 'jpg';

        fileName = `${story.instagram_username}-${date}.${fileExtension}`;

      }



      // Dosyayı indir

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement('a');

      link.href = url;

      link.setAttribute('download', fileName);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);

    } catch (err: any) {

      console.error('Story indirme hatası:', err);

      alert(err.response?.data?.message || 'Story indirilemedi. Dosya mevcut olmayabilir.');

    }

  };

  const handleDownloadPost = async (postId: number, post: any) => {
    try {
      const params: Record<string, string> = {};
      if (password) params.password = password;
      const response = await axios.get(`/api/public/report/${token}/posts/${postId}/download`, {
        params,
        responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `post-${post.shortcode || postId}.dat`;
      if (contentDisposition) {
        const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
        const plain = /filename="([^"]+)"/i.exec(contentDisposition);
        if (star?.[1]) {
          fileName = decodeURIComponent(star[1].replace(/['"]/g, '').trim());
        } else if (plain?.[1]) {
          fileName = plain[1];
        }
      } else {
        const ext = post.is_reel ? 'mp4' : 'jpg';
        fileName = `${post.instagram_username}-${postId}.${ext}`;
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Gönderi indirme hatası:', err);
      let msg = err.response?.data?.message;
      if (!msg && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j.message;
        } catch {
          msg = undefined;
        }
      }
      alert(msg || 'Gönderi indirilemedi. Dosya mevcut olmayabilir.');
    }
  };

  const handleDownloadAllStories = async () => {

    if (!campaign) return;

    
    
    const zipStories = stories.filter((s) => reportCountedFromRawStory(s));

    
    
    if (zipStories.length === 0) {

      alert('İndirilecek story yok.');

      return;

    }



    setZipModalContext('stories');
    setZipDownloadDialog(true);

    setZipProgress({ step: '🗂️ Hazırlanıyor...', percent: 0 });



    try {

      await new Promise(resolve => setTimeout(resolve, 500));

      setZipProgress({ step: `🗂️ ${zipStories.length} story toplanıyor...`, percent: 20 });

      
      
      await new Promise(resolve => setTimeout(resolve, 500));

      setZipProgress({ step: '🗜️ ZIP arşivi oluşturuluyor...', percent: 40 });

      
      
      // Public ZIP download endpoint (campaign detail ile aynı mantık)

      const response = await axios.post(

        `/api/stories/public/download-campaign/${token}`, 

        { password: password || '' },

        {

          responseType: 'blob',

          onDownloadProgress: (progressEvent) => {

            if (progressEvent.total) {

              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

              const downloadPercent = 40 + (percentCompleted * 0.5);

              setZipProgress({

                step: `Storyler indiriliyor... ${Math.round(progressEvent.loaded / 1024 / 1024)}MB / ${Math.round(progressEvent.total / 1024 / 1024)}MB`,

                percent: downloadPercent

              });

            }

          }

        }

      );



      setZipProgress({ step: 'Dosya kaydediliyor...', percent: 95 });

      
      
      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement('a');

      link.href = url;

      link.setAttribute('download', `${campaign.title}_stories.zip`);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);



      setZipProgress({ step: '✅ Tamamlandı!', percent: 100 });

      await new Promise(resolve => setTimeout(resolve, 1000));

      setZipDownloadDialog(false);

    } catch (err: any) {

      setZipProgress({ step: '❌ Hata oluştu!', percent: 0 });

      await new Promise(resolve => setTimeout(resolve, 2000));

      setZipDownloadDialog(false);

      console.error('ZIP indirme hatası:', err);

      alert(err.response?.data?.message || "Story'ler indirilirken hata oluştu");

    }

  };

  const handleDownloadAllTrackedPosts = async () => {
    if (!campaign) return;
    setZipModalContext('posts');
    setZipDownloadDialog(true);
    setZipProgress({ step: '🗂️ Hazırlanıyor...', percent: 0 });
    try {
      await new Promise((resolve) => setTimeout(resolve, 400));
      setZipProgress({ step: '🗜️ ZIP arşivi oluşturuluyor...', percent: 40 });
      const response = await axios.post(
        `/api/public/report/${token}/download-tracked-posts`,
        { password: password || '' },
        {
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setZipProgress({
                step: `Gönderiler indiriliyor... ${Math.round(progressEvent.loaded / 1024 / 1024)}MB / ${Math.round(progressEvent.total / 1024 / 1024)}MB`,
                percent: 40 + percentCompleted * 0.5
              });
            }
          }
        }
      );
      setZipProgress({ step: 'Dosya kaydediliyor...', percent: 95 });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] as string | undefined;
      let fname = `${campaign.title}_posts.zip`;
      if (cd) {
        const m = /filename\*?=(?:UTF-8'')?["']?([^\"';]+)/i.exec(cd);
        if (m?.[1]) fname = decodeURIComponent(m[1].replace(/['"]/g, ''));
      }
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setZipProgress({ step: '✅ Tamamlandı!', percent: 100 });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setZipDownloadDialog(false);
    } catch (err: any) {
      setZipProgress({ step: '❌ Hata oluştu!', percent: 0 });
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setZipDownloadDialog(false);
      let msg = err?.response?.data?.message;
      if (!msg && err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          msg = j.message;
        } catch {
          msg = undefined;
        }
      }
      alert(msg || 'Gönderiler indirilirken hata oluştu.');
    }
  };



  if (loading && !campaign) {

    return (

      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">

        <CircularProgress />

      </Box>

    );

  }



  if (error) {

    return (

      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={3}>

        <Alert severity="error" sx={{ maxWidth: 600 }}>

          {error}

        </Alert>

      </Box>

    );

  }



  return (

    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3 }}>

        {/* Header */}

        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>

          <CardContent sx={{ p: 4 }}>

            <Box display="flex" alignItems="center" gap={2} mb={2}>

              <CampaignIcon sx={{ fontSize: 48 }} />

              <Box>

                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>

                  {campaign?.title}

                </Typography>

                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>

                  Influencer Kampanya Raporu

                </Typography>

              </Box>

            </Box>

            {campaign?.description && (

              <Typography variant="body1" sx={{ mt: 2, opacity: 0.95 }}>

                {campaign.description}

              </Typography>

            )}

            
            
            {/* Kampanya Tarihleri */}

            <Box display="flex" gap={3} mt={3} sx={{ opacity: 0.9, flexWrap: 'wrap' }}>
              <Typography variant="body2">
 
 Başlangıç: {campaign?.start_date ? new Date(campaign.start_date).toLocaleDateString('tr-TR') : '-'}
              </Typography>
              <Typography variant="body2">
                Bitiş: {campaign?.end_date ? new Date(campaign.end_date).toLocaleDateString('tr-TR') : '-'}
              </Typography>
            </Box>

            {autoRefresh && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.28)',
                  borderRadius: 1
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <AutorenewIcon sx={{ fontSize: 22, opacity: 0.95 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>
                    Otomatik rapor güncellemesi
                  </Typography>
                </Box>
                {autoRefresh.scan_running_now && (
                  <LinearProgress
                    sx={{ mb: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }}
                  />
                )}
                <Typography variant="body2" sx={{ opacity: 0.96, lineHeight: 1.55 }}>
                  {autoRefresh.message}
                </Typography>
                {autoRefresh.status_key === 'countdown' &&
                  liveMinutesUntilRefresh != null &&
                  !autoRefresh.scan_running_now && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.88 }}>
                      {liveMinutesUntilRefresh > 0
                        ? `Kalan süre (tahmini): yaklaşık ${liveMinutesUntilRefresh} dakika`
                        : 'Sıradaki kontrol her an başlayabilir.'}
                    </Typography>
                  )}
                {autoRefresh.effective && autoRefresh.last_check_at && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.75 }}>
                    Son otomatik kontrol:{' '}
                    {new Date(autoRefresh.last_check_at).toLocaleString('tr-TR', {
                      dateStyle: 'short',
                      timeStyle: 'short'
                    })}
                    {autoRefresh.interval_minutes
                      ? ` · Aralık: her ${autoRefresh.interval_minutes} dakikada bir`
                      : ''}
                  </Typography>
                )}
              </Box>
            )}
 
          </CardContent>

        </Card>



        {/* Kriter Bazlı İstatistikler: Story listesi üstüne taşındı */}







        {/* Password Dialog */}

        <Dialog open={passwordDialogOpen} maxWidth="sm" fullWidth>

          <DialogTitle>

            <Box display="flex" alignItems="center" gap={1}>

              <LockIcon color="primary" />

              <Typography variant="h6">Şifre Gerekli</Typography>

            </Box>

          </DialogTitle>

          <DialogContent>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>

              Bu rapor Şifre ile korunmaktadır. Devam etmek için şifre giriniz.

            </Typography>

            <TextField

              fullWidth

              type="password"

              label="Şifre"

              value={password}

              onChange={(e) => setPassword(e.target.value)}

              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}

              autoFocus

            />

          </DialogContent>

          <DialogActions>

            <Button onClick={handlePasswordSubmit} variant="contained" disabled={!password.trim()}>

              Giriş Yap

            </Button>

          </DialogActions>

        </Dialog>

        {stats && (

          <>
            {reportConfig.content_tracking_mode === 'both' && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={outerTab}
                  onChange={(_, v) => setOuterTab(v as 'story' | 'post')}
                  variant="fullWidth"
                  aria-label="Hikaye ve gönderi raporu"
                >
                  <Tab icon={<AutoStoriesIcon />} iconPosition="start" label="Hikaye Raporu" value="story" />
                  <Tab icon={<CollectionsIcon />} iconPosition="start" label="Gönderi Raporu" value="post" />
                </Tabs>
              </Box>
            )}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>

              <Tabs

                value={activeTab}

                onChange={handleTabChange}

                variant="scrollable"

                scrollButtons="auto"

                aria-label="Public report sections"

              >

                <Tab label="Genel Bakış" {...tabProps(0)} />

                <Tab label={`Influencerlar (${stats.total_influencers})`} {...tabProps(1)} />

                <Tab
                  label={
                    reportConfig.content_tracking_mode === 'both'
                      ? reportScope === 'story'
                        ? `Story'ler (${stats.total_stories})`
                        : `Gönderiler (${stats.total_posts ?? 0})`
                      : reportConfig.show_stories && reportConfig.show_posts
                        ? `Story & Gönderi (${stats.total_stories}/${stats.total_posts ?? 0})`
                        : reportConfig.show_posts
                          ? `Gönderiler (${stats.total_posts ?? 0})`
                          : `Story'ler (${stats.total_stories})`
                  }
                  {...tabProps(2)}
                />

                <Tab label="Rapor" {...tabProps(3)} />

              </Tabs>

            </Box>

            <Box display={{ xs: 'flex', md: 'none' }} justifyContent="center" mb={2}>
              <Chip
                label="Kaydırarak diğer sekmeleri görüntüleyebilirsiniz"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ opacity: 0.75 }}
              />
            </Box>


            <TabPanel value={activeTab} index={0}>

            {/* İstatistikler */}

            {reportScope === 'post' && reportConfig.show_posts ? (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            {stats.total_influencers}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Influencer
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Kampanyaya eklenmiş tüm influencer sayısı.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <GroupsIcon color="secondary" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                            {(stats.total_followers ?? 0).toLocaleString('tr-TR')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Toplam Takipçi
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Listede yer alan takipçi toplamı.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CheckIcon color="success" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {participationScoped.shared}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Paylaşan
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            En az bir gönderi kaydı olan influencer.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <CancelIcon color="error" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            {participationScoped.notShared}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Paylaşmayan
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Henüz gönderi kaydı olmayan influencer.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <PhotoIcon color="info" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                            {(stats.total_posts ?? postScopeOverviewKpis.totalPosts).toLocaleString('tr-TR')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tespit Edilen Gönderi
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Toplam feed / Reels kayıt sayısı.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3} sx={{ mb: 3 }}>

              {/* Satır 1: 6 + 6 — toplam kişi, toplam takipçi */}
              <Grid item xs={12} md={6}>

                <Card>

                  <CardContent>

                    <Box display="flex" alignItems="center" gap={2}>

                      <PeopleIcon color="primary" sx={{ fontSize: 40 }} />

                      <Box>

                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>

                          {stats.total_influencers}

                        </Typography>

                        <Typography variant="body2" color="text.secondary">

                          Toplam Influencer

                        </Typography>

                        <Typography variant="caption" color="text.secondary">

                          Kampanyaya eklenmiş tüm influencer sayısını gösterir.

                        </Typography>

                      </Box>

                    </Box>

                  </CardContent>

                </Card>

              </Grid>

              <Grid item xs={12} md={6}>

                <Card>

                  <CardContent>

                    <Box display="flex" alignItems="center" gap={2}>

                      <GroupsIcon color="secondary" sx={{ fontSize: 40 }} />

                      <Box>

                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>

                          {(stats.total_followers ?? 0).toLocaleString('tr-TR')}

                        </Typography>

                        <Typography variant="body2" color="text.secondary">

                          Toplam Takipçi

                        </Typography>

                        <Typography variant="caption" color="text.secondary">

                          Listede yer alan tüm influencerların takipçi sayıları toplamı.

                        </Typography>

                      </Box>

                    </Box>

                  </CardContent>

                </Card>

              </Grid>

              {/* Satır 2: 4 + 4 + 4 */}
              <Grid item xs={12} md={4}>

                <Card>

                  <CardContent>

                    <Box display="flex" alignItems="center" gap={2}>

                      <CheckIcon color="success" sx={{ fontSize: 40 }} />

                      <Box>

                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>

                          {participationScoped.shared}

                        </Typography>

                        <Typography variant="body2" color="text.secondary">

                          Paylaşan

                        </Typography>

                        <Typography variant="caption" color="text.secondary">

                          Raporda en az bir paylaşımı sayılan influencer sayısı.

                        </Typography>

                      </Box>

                    </Box>

                  </CardContent>

                </Card>

              </Grid>

              <Grid item xs={12} md={4}>

                <Card>

                  <CardContent>

                    <Box display="flex" alignItems="center" gap={2}>

                      <CancelIcon color="error" sx={{ fontSize: 40 }} />

                      <Box>

                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>

                          {participationScoped.notShared}

                        </Typography>

                        <Typography variant="body2" color="text.secondary">

                          Paylaşmayan

                        </Typography>

                        <Typography variant="caption" color="text.secondary">

                          Raporda paylaşımı sayılmayan influencer sayısı.

                        </Typography>

                      </Box>

                    </Box>

                  </CardContent>

                </Card>

              </Grid>

              <Grid item xs={12} md={4}>

                <Card>

                  <CardContent>

                    <Box display="flex" alignItems="center" gap={2}>

                      <PhotoIcon color="info" sx={{ fontSize: 40 }} />

                      <Box>

                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>

                          {stats.total_stories}

                        </Typography>

                        <Typography variant="body2" color="text.secondary">

                          Tespit Edilen Story

                        </Typography>

                        <Typography variant="caption" color="text.secondary">

                          Kampanya kriterleriyle eşleşen story adedini gösterir.

                        </Typography>

                      </Box>

                    </Box>

                  </CardContent>

                </Card>

              </Grid>

              {reportConfig.show_posts && reportScope === 'story' && reportConfig.content_tracking_mode !== 'both' && (
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <PhotoIcon color="secondary" sx={{ fontSize: 40 }} />
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                            {stats.total_posts ?? enrichedPosts.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tespit edilen gönderi / Reels
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Hedef kriterlerle eşleşen feed kayıtları.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

            </Grid>
            )}



            {/* Başarı Oranı */}

            <Card sx={{ mb: 3 }}>

              <CardContent>

                <Typography variant="h6" gutterBottom>

                  Katılım Oranı

                </Typography>

                <Box display="flex" alignItems="center" gap={2}>

                  <Box flexGrow={1}>

                    <LinearProgress 

                      variant="determinate" 

                      value={
                        participationScoped.total
                          ? (participationScoped.shared / participationScoped.total) * 100
                          : 0
                      }

                      sx={{ height: 10, borderRadius: 5 }}

                      color="success"

                    />

                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>

                    {participationScoped.total
                      ? Math.round((participationScoped.shared / participationScoped.total) * 100)
                      : 0}
                    %

                  </Typography>

                </Box>

              </CardContent>

            </Card>



            {campaign && (

              <Card sx={{ mb: 3 }}>

                <CardContent>

                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>

                    Kampanya Kriterleri

                  </Typography>

                  <Divider sx={{ my: 2 }} />



                  <Grid container spacing={2}>

                    {campaign.target_hashtags && campaign.target_hashtags.length > 0 && (

                      <Grid item xs={12} md={4}>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>

                          # Hashtag'ler

                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap={0.5}>

                          {campaign.target_hashtags.map((tag, idx) => (

                            <Chip key={idx} label={tag} size="small" color="primary" variant="outlined" />

                          ))}

                        </Box>

                      </Grid>

                    )}



                    {campaign.target_mentions && campaign.target_mentions.length > 0 && (

                      <Grid item xs={12} md={4}>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>

                          @ Mention'lar

                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap={0.5}>

                          {campaign.target_mentions.map((mention, idx) => (

                            <Chip key={idx} label={mention} size="small" color="secondary" variant="outlined" />

                          ))}

                        </Box>

                      </Grid>

                    )}



                    {campaign.target_links && campaign.target_links.length > 0 && (

                      <Grid item xs={12} md={4}>

                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>

                          Link'ler

                        </Typography>

                        <Box display="flex" flexWrap="wrap" gap={0.5}>

                          {campaign.target_links.map((link: any, idx) => (

                            <Chip

                              key={idx}

                              label={typeof link === 'string' ? link : `${link.url} (${link.matchType === 'contains' ? 'içeren' : 'Tam'})`}

                              size="small"

                              color="info"

                              variant="outlined"

                            />

                          ))}

                        </Box>

                      </Grid>

                    )}

                    {campaign.target_caption_keywords && campaign.target_caption_keywords.length > 0 && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Caption ifadeleri
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {campaign.target_caption_keywords.map((phrase, idx) => (
                            <Chip key={idx} label={phrase} size="small" color="success" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                  </Grid>



                  {!campaign.target_hashtags?.length &&
                    !campaign.target_mentions?.length &&
                    !campaign.target_links?.length &&
                    !(campaign.target_caption_keywords && campaign.target_caption_keywords.length > 0) && (

                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>

                      Bu kampanyada kriter tanımlanmamış.

                    </Typography>

                  )}

                </CardContent>

              </Card>

            )}



            </TabPanel>



            <TabPanel value={activeTab} index={1}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Influencer Performans Raporu
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    ℹ️ Bu tablo kampanyaya katılan influencerları ve genel performans tablosunu gösterir.
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="İsim veya kullanıcı adı ile ara…"
                  value={influencerPerformanceSearch}
                  onChange={(e) => setInfluencerPerformanceSearch(e.target.value)}
                  sx={{ mt: 1, mb: 1.5, maxWidth: 440 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
                {influencerPerformanceSearch.trim() ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {filteredInfluencersForPerformance.length} / {influencers.length} influencer listeleniyor
                  </Typography>
                ) : null}
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Influencer</TableCell>
                        <TableCell align="center">Durum</TableCell>
                        <TableCell align="center">
                          {reportScope === 'story' ? 'Story sayısı' : 'Gönderi sayısı'}
                        </TableCell>
                        <TableCell align="center">
                          {reportScope === 'story' ? 'İlk story' : 'İlk gönderi'}
                        </TableCell>
                        <TableCell align="center">
                          {reportScope === 'story' ? 'Son story' : 'Son gönderi'}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredInfluencersForPerformance.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                              Aramanızla eşleşen influencer bulunamadı.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                      filteredInfluencersForPerformance.map((influencer) => (
                        <TableRow key={influencer.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar 
                                src={influencer.profile_picture} 
                                alt={influencer.instagram_username}
                                sx={{ width: 40, height: 40 }}
                              >
                                {influencer.instagram_username[0].toUpperCase()}
                              </Avatar>
                              <Box>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {influencer.full_name || influencer.instagram_username}
                                      {!!influencer.is_verified && (
                                        <Tooltip title="Instagram doğrulanmış hesap" placement="top">
                                          <VerifiedIcon fontSize="small" sx={{ color: '#3897f0' }} />
                                        </Tooltip>
                                      )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                      <Link
                                        href={`https://www.instagram.com/${influencer.instagram_username}/`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        underline="hover"
                                      >
                                        @{influencer.instagram_username}
                                      </Link>
                                      {(influencer.follower_count !== null && influencer.follower_count !== undefined && influencer.follower_count > 0) && (
                                        <> • {influencer.follower_count.toLocaleString('tr-TR')} takipçi</>
                                      )}
                                </Typography>
                                  </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {reportScope === 'story' ? (
                              influencerIdsWithReportStories.has(Number(influencer.id)) ? (
                                <Chip label="Paylaştı" color="success" size="small" icon={<CheckIcon />} />
                              ) : (
                                <Chip label="Paylaşmadı" color="error" size="small" icon={<CancelIcon />} />
                              )
                            ) : (postCountByInfluencerId.get(Number(influencer.id)) ??
                                (influencer.post_count != null ? Number(influencer.post_count) : 0)) > 0 ? (
                              <Chip label="Paylaştı" color="success" size="small" icon={<CheckIcon />} />
                            ) : (
                              <Chip label="Paylaşmadı" color="error" size="small" icon={<CancelIcon />} />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="h6"
                              color={
                                (reportScope === 'story'
                                  ? storyCountByInfluencerId.get(Number(influencer.id)) ?? 0
                                  : postCountByInfluencerId.get(Number(influencer.id)) ??
                                    (influencer.post_count != null ? Number(influencer.post_count) : 0)) > 0
                                  ? 'primary'
                                  : 'text.secondary'
                              }
                            >
                              {reportScope === 'story'
                                ? storyCountByInfluencerId.get(Number(influencer.id)) ?? 0
                                : postCountByInfluencerId.get(Number(influencer.id)) ??
                                  (influencer.post_count != null ? Number(influencer.post_count) : 0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {(reportScope === 'story' ? influencer.first_story_date : influencer.first_post_date) ? (
                              <Typography variant="body2">
                                {new Date(
                                  (reportScope === 'story'
                                    ? influencer.first_story_date
                                    : influencer.first_post_date) as string
                                ).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {(reportScope === 'story' ? influencer.last_story_date : influencer.last_post_date) ? (
                              <Typography variant="body2">
                                {new Date(
                                  (reportScope === 'story'
                                    ? influencer.last_story_date
                                    : influencer.last_post_date) as string
                                ).toLocaleDateString('tr-TR', {
                                  day: '2-digit',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
            </TabPanel>





            <TabPanel value={activeTab} index={3}>
              {!stats ? (
                <Alert severity="info" sx={{ mt: 3 }}>
                  Bu kampanya için kriter analizi verisi bulunamadı.
                </Alert>
              ) : (
                <>
              <Box
                 display="flex"
                 flexDirection={{ xs: 'column', md: 'row' }}
                 alignItems={{ xs: 'stretch', md: 'center' }}
                 justifyContent="space-between"
                 gap={2}
                 mb={2}
               >
                 <Typography variant="h6" sx={{ fontWeight: 600 }}>
                   Kampanya Özeti
                 </Typography>
                 <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    alignSelf: { xs: 'flex-start', md: 'center' },
                    px: 3,
                    boxShadow: '0 12px 30px rgba(59,130,246,0.25)'
                  }}
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAnalysisPdf}
                >
                  PDF Olarak İndir
                </Button>
              </Box>
 
              <Box
                ref={analysisRef}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  background: 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(226,232,240,0.95) 100%)',
                  borderRadius: 4,
                  p: { xs: 2, md: 3 },
                  boxShadow: '0 30px 60px rgba(15,23,42,0.08)',
                  border: '1px solid rgba(148,163,184,0.35)'
                }}
              >
                {reportScope === 'story' && (
                <>
                <Grid container spacing={3}>
                  {[
                    {
                      label: 'Toplam Influencer',
                      value: stats.total_influencers,
                      gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      icon: <PeopleIcon sx={{ fontSize: 30 }} />
                    },
                    {
                      label: 'Toplam Takipçi',
                      value: stats.total_followers ?? 0,
                      gradient: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
                      icon: <GroupsIcon sx={{ fontSize: 28 }} />
                    }
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} md={6}>
                      <Card
                        sx={{
                          height: '100%',
                          color: '#fff',
                          backgroundImage: item.gradient,
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 20px 35px rgba(30,64,175,0.25)',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            top: -40,
                            right: -40,
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            {item.icon}
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                            {item.value.toLocaleString('tr-TR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={3}>
                  {[
                    {
                      label: 'Paylaşan Influencer',
                      value: participationScoped.shared,
                      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      icon: <CheckIcon sx={{ fontSize: 28 }} />
                    },
                    {
                      label: 'Paylaşmayan Influencer',
                      value: participationScoped.notShared,
                      gradient: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)',
                      icon: <CancelIcon sx={{ fontSize: 28 }} />
                    },
                    {
                      label: 'Tespit Edilen Story',
                      value: stats.total_stories,
                      gradient: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 100%)',
                      icon: <PhotoIcon sx={{ fontSize: 28 }} />
                    }
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} md={4}>
                      <Card
                        sx={{
                          height: '100%',
                          color: '#fff',
                          backgroundImage: item.gradient,
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 20px 35px rgba(30,64,175,0.25)',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            top: -40,
                            right: -40,
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            {item.icon}
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                            {item.value.toLocaleString('tr-TR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Takipçi kırılımları
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Listede takipçi sayısı bilinen hesaplar üzerinden özetlenir.
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşanların takipçi toplamı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'success.main' }}>
                          {followerBreakdownScoped.sharedSum.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Kriter uyumlu paylaşım yapan hesaplar
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşmayanların takipçi toplamı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'error.main' }}>
                          {followerBreakdownScoped.notSharedSum.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Henüz uyumlu paylaşım olmayan hesaplar
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ortalama takipçi / influencer
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {Math.round(followerBreakdownScoped.avgPerInfluencer).toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tüm liste için aritmetik ortalama
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşanların toplam takipçi payı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                          %{followerBreakdownScoped.pctSharedOfTotal.toFixed(1).replace('.', ',')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Toplam takipçi içinde paylaşanların oranı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Tespit Edilen Story
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {(stats?.total_stories ?? reportCountedStories.length).toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Raporda sayılan toplam paylaşım (kriterli veya kayıtlı)
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Toplam Kriter Eşleşmesi
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {totalCriteriaMatches.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hashtag + Mention + Link toplamı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ortalama Kriter / Story
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {keywordStats.avgPerStory.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {keywordStats.uniqueKeywords.toLocaleString('tr-TR')} benzersiz kriter
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Katılım Oranı
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: 'success.main' }}>
                          {stats?.total_influencers ? Math.round(influencerAnalytics.shareRate || 0) : 0}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Paylaşım yapan influencer oranı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Tarih Bazlı Hikaye Analizi
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Paylaşım yapılan tüm günler ve kriterlere göre dağılım
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {storyDailySummary.length ? (
                          <>
                            <TableContainer component={Paper} variant="outlined">
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="18%">Tarih</TableCell>
                                    <TableCell width="14%">Gün</TableCell>
                                    <TableCell align="center" width="12%">Toplam Hikaye</TableCell>
                                    <TableCell align="center" width="14%">Aktif Influencer</TableCell>
                                    <TableCell align="center" width="14%">Hashtag İçeren</TableCell>
                                    <TableCell align="center" width="14%">Mention İçeren</TableCell>
                                    <TableCell align="center" width="14%">Link İçeren</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {storyDailySummary
                                    .slice(
                                      dailySummaryPage * DAILY_SUMMARY_ROWS_PER_PAGE,
                                      dailySummaryPage * DAILY_SUMMARY_ROWS_PER_PAGE + DAILY_SUMMARY_ROWS_PER_PAGE
                                    )
                                    .map((entry) => (
                                      <TableRow key={entry.date} hover>
                                        <TableCell>
                                          <Typography variant="body2" fontWeight={600}>
                                            {new Date(entry.date).toLocaleDateString('tr-TR', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric'
                                            })}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textTransform: 'capitalize' }}>{entry.dayName}</TableCell>
                                        <TableCell align="center">
                                          <Chip label={entry.storyCount} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell align="center">{entry.activeCount}</TableCell>
                                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                          {entry.hashtagCount}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                          {entry.mentionCount}
                                        </TableCell>
                                        <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                          {entry.linkCount}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            {storyDailySummary.length > DAILY_SUMMARY_ROWS_PER_PAGE && (
                              <TablePagination
                                component="div"
                                count={storyDailySummary.length}
                                rowsPerPage={DAILY_SUMMARY_ROWS_PER_PAGE}
                                page={dailySummaryPage}
                                onPageChange={handleDailySummaryPageChange}
                                rowsPerPageOptions={[DAILY_SUMMARY_ROWS_PER_PAGE]}
                                labelRowsPerPage=""
                                sx={{ mt: 1 }}
                              />
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Henüz raporda sayılan paylaşım bulunmuyor.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Story Kırılımı ve İçgörüler
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Video/görsel oranları, öne çıkan günler ve popüler kriterler
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {hasReportStories ? (
                          <Box display={{ xs: 'block', md: 'grid' }} gridTemplateColumns={{ md: '1fr 1fr' }} gap={3}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {[
                                { label: 'Video', value: storyTypeBreakdown.video, percent: storyTypeBreakdown.percentages.video, color: 'secondary.main' },
                                { label: 'Görsel', value: storyTypeBreakdown.image, percent: storyTypeBreakdown.percentages.image, color: 'info.main' }
                              ].map((item) => (
                                <Box key={item.label}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="body2" fontWeight={500}>
                                      {item.label}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {item.value.toLocaleString('tr-TR')} • %{Math.round(item.percent)}
                                    </Typography>
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={item.percent}
                                    sx={{
                                      height: 8,
                                      borderRadius: 4,
                                      backgroundColor: 'rgba(0,0,0,0.06)',
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        backgroundColor: item.color
                                      }
                                    }}
                                  />
                                </Box>
                              ))}

                              <Divider sx={{ my: 2 }} />

                              <Typography variant="subtitle2" gutterBottom>
                                Anahtar Kelime Yoğunluğu
                              </Typography>
                              <Typography variant="body2">
                                Toplam <strong>{keywordStats.totalKeywords.toLocaleString('tr-TR')}</strong> kriter eşleşmesi
                              </Typography>
                              <Typography variant="body2">
                                Story başına ortalama <strong>{keywordStats.avgPerStory.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                              </Typography>
                              <Typography variant="body2">
                                {keywordStats.uniqueKeywords.toLocaleString('tr-TR')} benzersiz anahtar kelime tespit edildi
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {storyHighlights ? (
                                <>
                                  <Typography variant="subtitle2">
                                    Öne Çıkan Günler
                                  </Typography>
                                  <Typography variant="body2">
                                    En yoğun gün: <strong>{new Date(storyHighlights.mostActive.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}</strong> • {storyHighlights.mostActive.count} story
                                  </Typography>
                                  {storyHighlights.leastActive && storyHighlights.leastActive.date !== storyHighlights.mostActive.date && (
                                    <Typography variant="body2" color="text.secondary">
                                      En sakin gün: {new Date(storyHighlights.leastActive.date).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })} • {storyHighlights.leastActive.count} story
                                    </Typography>
                                  )}
                                  <Typography variant="body2">
                                    Günlük ortalama story: <strong>{storyHighlights.avgPerDay.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</strong>
                                  </Typography>

                                  {storyHighlights.topKeywords.length ? (
                                    <Box>
                                      <Typography variant="subtitle2" sx={{ mt: 1 }}>
                                        En Popüler Anahtar Kelimeler
                                      </Typography>
                                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                                        {storyHighlights.topKeywords.map((item) => (
                                          <Chip key={item.key} label={`${item.key} • ${item.count}`} size="small" color="primary" variant="outlined" />
                                        ))}
                                      </Box>
                                    </Box>
                                  ) : null}

                                  <Divider sx={{ my: 1.5 }} />

                                  <Typography variant="subtitle2">
                                    En Çok Eşleşen Kriterler
                                  </Typography>
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    {topHashtag ? (
                                      <Typography variant="body2">
                                        Hashtag: <strong>{topHashtag.key.startsWith('#') ? topHashtag.key : `#${topHashtag.key}`}</strong> • {topHashtag.story_count} story
                                      </Typography>
                                    ) : null}
                                    {topMention ? (
                                      <Typography variant="body2">
                                        Mention: <strong>{topMention.key.startsWith('@') ? topMention.key : `@${topMention.key}`}</strong> • {topMention.story_count} story
                                      </Typography>
                                    ) : null}
                                    {topLink ? (
                                      <Typography variant="body2">
                                        Link: <strong>{topLink.key}</strong> • {topLink.story_count} story
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Story içgörülerini hesaplamak için yeterli veri bulunamadı.
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Görselleştirilecek veri bulunamadı.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Öne Çıkan Influencerlar
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Takipçi sayısına göre ilk 5 influencer
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {influencerAnalytics.topPerformers.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {influencerAnalytics.topPerformers.map((inf) => (
                              <Box
                                key={inf.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  p: 1,
                                  borderRadius: 1,
                                  bgcolor: 'rgba(56, 151, 240, 0.08)'
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {inf.full_name || `@${inf.instagram_username}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    @{inf.instagram_username}
                                  </Typography>
                                </Box>
                                <Box textAlign="right">
                                  <Typography variant="body2" fontWeight={600} color="primary">
                                    {(inf.follower_count || 0).toLocaleString('tr-TR')} takipçi
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {storyCountByInfluencerId.get(Number(inf.id)) ?? 0} paylaşım
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Influencer bulunmuyor.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Katılım Analizi
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ortalama performans ve en aktif katılım
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        {influencerAnalytics.total ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body2">
                              Ortalama <strong>{influencerAnalytics.averageStories.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> story / influencer
                            </Typography>
                            <Typography variant="body2">
                              Medyan <strong>{influencerAnalytics.medianStories.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</strong>
                            </Typography>
                            <Typography variant="body2">
                              Doğrulanmış hesap katılımı: <strong>{Math.round(influencerAnalytics.verifiedShareRate || 0)}%</strong>
                            </Typography>
                            <Divider sx={{ my: 1.5 }} />
                            <Typography variant="subtitle2" gutterBottom>
                              En Yoğun Paylaşım Saat Aralığı
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                              Tarih/saat bilgisi olan paylaşımlar · Türkiye saati
                            </Typography>
                            {peakPostingHourRange ? (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {peakPostingHourRange.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Bu 2 saatlik dilimde {peakPostingHourRange.countInWindow.toLocaleString('tr-TR')} paylaşım
                                  {peakPostingHourRange.datedWithTimestamp > 0
                                    ? ` · zaman bilgisi olan ${peakPostingHourRange.datedWithTimestamp.toLocaleString(
                                        'tr-TR'
                                      )} paylaşımdan bu aralığa düşen oran %${Math.round(
                                        (peakPostingHourRange.countInWindow / peakPostingHourRange.datedWithTimestamp) * 100
                                      )}`
                                    : ''}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Tarih/saat bilgisi olan paylaşım yok.
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Influencer verisi bulunmuyor.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Kriter Bazlı Dağılım
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      En çok eşleşen kriterler ve etkileşimleri
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          # Hashtag
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {criteriaSummary.hashtags.total.toLocaleString('tr-TR')} story • {criteriaSummary.hashtags.unique.toLocaleString('tr-TR')} benzersiz
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        {criteriaSummary.hashtags.top.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {criteriaSummary.hashtags.top.map((item) => (
                              <Box key={item.key} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">
                                  {item.key?.startsWith('#') ? item.key : `#${item.key}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.story_count} story • %{item.ratio}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Hashtag verisi bulunamadı.
                          </Typography>
                        )}
                        {stats?.criteria?.hashtags?.length ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 260 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Etiket</TableCell>
                                  <TableCell align="right">Story</TableCell>
                                  <TableCell align="right">Influencer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {stats.criteria.hashtags.slice(0, 20).map((row, i) => (
                                  <TableRow key={`${row.key}-${i}`}>
                                    <TableCell>{row.key?.startsWith('#') ? row.key : `#${row.key}`}</TableCell>
                                    <TableCell align="right">{row.story_count}</TableCell>
                                    <TableCell align="right">{row.influencer_count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          @ Mention
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {criteriaSummary.mentions.total.toLocaleString('tr-TR')} story • {criteriaSummary.mentions.unique.toLocaleString('tr-TR')} benzersiz
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        {criteriaSummary.mentions.top.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {criteriaSummary.mentions.top.map((item) => (
                              <Box key={item.key} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">
                                  {item.key?.startsWith('@') ? item.key : `@${item.key}`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.story_count} story • %{item.ratio}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Mention verisi bulunamadı.
                          </Typography>
                        )}
                        {stats?.criteria?.mentions?.length ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 260 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Hesap</TableCell>
                                  <TableCell align="right">Story</TableCell>
                                  <TableCell align="right">Influencer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {stats.criteria.mentions.slice(0, 20).map((row, i) => (
                                  <TableRow key={`${row.key}-${i}`}>
                                    <TableCell>{row.key?.startsWith('@') ? row.key : `@${row.key}`}</TableCell>
                                    <TableCell align="right">{row.story_count}</TableCell>
                                    <TableCell align="right">{row.influencer_count}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Link
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {criteriaSummary.links.total.toLocaleString('tr-TR')} story • {criteriaSummary.links.unique.toLocaleString('tr-TR')} benzersiz
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        {criteriaSummary.links.top.length ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {criteriaSummary.links.top.map((item) => (
                              <Box key={item.key} display="flex" justifyContent="space-between" alignItems="center">
                                <Typography
                                  variant="body2"
                                  sx={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {item.key}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.story_count} story • %{item.ratio}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Link verisi bulunamadı.
                          </Typography>
                        )}
                        {stats.criteria?.links?.length ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, maxHeight: 260 }}>
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell>Link</TableCell>
                                  <TableCell align="right">Story</TableCell>
                                  <TableCell align="right">Influencer</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {stats.criteria.links.slice(0, 20).map((row, i) => (
                                  <TableRow key={`${row.key}-${i}`}>
                                    <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {row.key}
                                    </TableCell>
                                    <TableCell align="right">{row.story_count}
                                    </TableCell>
                                    <TableCell align="right">{row.influencer_count}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : null}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
                </>
                )}

                {reportScope === 'post' && reportConfig.show_posts && (
                <>
                <Grid container spacing={3}>
                  {[
                    {
                      label: 'Toplam Influencer',
                      value: stats.total_influencers,
                      gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                      icon: <PeopleIcon sx={{ fontSize: 30 }} />
                    },
                    {
                      label: 'Toplam Takipçi',
                      value: stats.total_followers ?? 0,
                      gradient: 'linear-gradient(135deg, #A855F7 0%, #7C3AED 100%)',
                      icon: <GroupsIcon sx={{ fontSize: 28 }} />
                    }
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} md={6}>
                      <Card
                        sx={{
                          height: '100%',
                          color: '#fff',
                          backgroundImage: item.gradient,
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 20px 35px rgba(30,64,175,0.25)',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            top: -40,
                            right: -40,
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            {item.icon}
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                            {item.value.toLocaleString('tr-TR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={3} sx={{ mt: 0 }}>
                  {[
                    {
                      label: 'Paylaşan Influencer',
                      value: participationScoped.shared,
                      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                      icon: <CheckIcon sx={{ fontSize: 28 }} />
                    },
                    {
                      label: 'Paylaşmayan Influencer',
                      value: participationScoped.notShared,
                      gradient: 'linear-gradient(135deg, #FB7185 0%, #F43F5E 100%)',
                      icon: <CancelIcon sx={{ fontSize: 28 }} />
                    },
                    {
                      label: 'Tespit Edilen Gönderi',
                      value: stats.total_posts ?? enrichedPosts.length,
                      gradient: 'linear-gradient(135deg, #38BDF8 0%, #0EA5E9 100%)',
                      icon: <PhotoIcon sx={{ fontSize: 28 }} />
                    }
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} md={4}>
                      <Card
                        sx={{
                          height: '100%',
                          color: '#fff',
                          backgroundImage: item.gradient,
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 20px 35px rgba(30,64,175,0.25)',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            top: -40,
                            right: -40,
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            {item.icon}
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                            {typeof item.value === 'number' ? item.value.toLocaleString('tr-TR') : item.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={3} sx={{ mt: 0 }}>
                  {[
                    {
                      label: 'Reels',
                      value: postScopeOverviewKpis.reels,
                      gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
                      icon: <VideocamIcon sx={{ fontSize: 28 }} />
                    },
                    {
                      label: 'Ort. gönderi / paylaşan',
                      value: postScopeOverviewKpis.avg,
                      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      icon: <CampaignIcon sx={{ fontSize: 28 }} />,
                      format: 'decimal' as const
                    }
                  ].map((item) => (
                    <Grid key={item.label} item xs={12} md={6}>
                      <Card
                        sx={{
                          height: '100%',
                          color: '#fff',
                          backgroundImage: item.gradient,
                          border: 'none',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 20px 35px rgba(30,64,175,0.25)',
                          '&:after': {
                            content: '""',
                            position: 'absolute',
                            width: 120,
                            height: 120,
                            top: -40,
                            right: -40,
                            backgroundColor: 'rgba(255,255,255,0.18)',
                            borderRadius: '50%'
                          }
                        }}
                      >
                        <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                              {item.label}
                            </Typography>
                            {item.icon}
                          </Box>
                          <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                            {item.format === 'decimal'
                              ? Number(item.value).toLocaleString('tr-TR', {
                                  minimumFractionDigits: 1,
                                  maximumFractionDigits: 1
                                })
                              : Number(item.value).toLocaleString('tr-TR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mt: 2 }}>
                  Takipçi kırılımları
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Gönderi kapsamında paylaşan / paylaşmayan influencerlara göre; listede takipçi bilgisi olan hesaplar.
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşanların takipçi toplamı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'success.main' }}>
                          {followerBreakdownScoped.sharedSum.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          En az bir gönderi kaydı olan hesaplar
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşmayanların takipçi toplamı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'error.main' }}>
                          {followerBreakdownScoped.notSharedSum.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Henüz gönderi kaydı olmayan hesaplar
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ortalama takipçi / influencer
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {Math.round(followerBreakdownScoped.avgPerInfluencer).toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Tüm liste için aritmetik ortalama
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card variant="outlined" sx={{ bgcolor: 'rgba(255,255,255,0.92)' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Paylaşanların toplam takipçi payı
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: 'primary.main' }}>
                          %{followerBreakdownScoped.pctSharedOfTotal.toFixed(1).replace('.', ',')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Toplam takipçi içinde paylaşanların oranı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Tespit Edilen Gönderi
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {(stats?.total_posts ?? enrichedPosts.length).toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Raporda sayılan toplam gönderi / Reels kaydı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Toplam Kriter Eşleşmesi
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {totalPostCriteriaMatches.toLocaleString('tr-TR')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hashtag + Mention + Link + Caption toplamı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ortalama Kriter / Gönderi
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                          {keywordPostStats.avgPerPost.toLocaleString('tr-TR', {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {keywordPostStats.uniqueKeywords.toLocaleString('tr-TR')} benzersiz kriter
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">
                          Katılım Oranı
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: 'success.main' }}>
                          {participationScoped.total
                            ? Math.round((participationScoped.shared / participationScoped.total) * 100)
                            : 0}
                          %
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Gönderi paylaşan influencer oranı
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                </>
                )}

                {reportScope === 'post' && reportConfig.show_posts && stats?.criteria_posts && (
                  <Card sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Gönderi / Reels — kriter dağılımı
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                        Feed ve Reels kayıtlarında hedef kriterlere göre özet
                      </Typography>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary">
                            # Hashtag
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {criteriaPostsSummary.hashtags.total.toLocaleString('tr-TR')} gönderi
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          {stats.criteria_posts?.hashtags?.length ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 220 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Etiket</TableCell>
                                    <TableCell align="right">Gönderi</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {stats.criteria_posts.hashtags.slice(0, 15).map((row, i) => (
                                    <TableRow key={`ph-${i}`}>
                                      <TableCell>{row.key?.startsWith('#') ? row.key : `#${row.key}`}</TableCell>
                                      <TableCell align="right">{row.post_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Veri yok
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary">
                            @ Mention
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {criteriaPostsSummary.mentions.total.toLocaleString('tr-TR')} gönderi
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          {stats.criteria_posts?.mentions?.length ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 220 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Hesap</TableCell>
                                    <TableCell align="right">Gönderi</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {stats.criteria_posts.mentions.slice(0, 15).map((row, i) => (
                                    <TableRow key={`pm-${i}`}>
                                      <TableCell>{row.key?.startsWith('@') ? row.key : `@${row.key}`}</TableCell>
                                      <TableCell align="right">{row.post_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Veri yok
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Link
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {criteriaPostsSummary.links.total.toLocaleString('tr-TR')} gönderi
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          {stats.criteria_posts?.links?.length ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 220 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Link</TableCell>
                                    <TableCell align="right">Gönderi</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {stats.criteria_posts.links.slice(0, 15).map((row, i) => (
                                    <TableRow key={`pl-${i}`}>
                                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.key}
                                      </TableCell>
                                      <TableCell align="right">{row.post_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Veri yok
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Caption
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {criteriaPostsSummary.captions.total.toLocaleString('tr-TR')} gönderi
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          {stats.criteria_posts?.caption_keywords?.length ? (
                            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 220 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>İfade</TableCell>
                                    <TableCell align="right">Gönderi</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {stats.criteria_posts.caption_keywords.slice(0, 15).map((row, i) => (
                                    <TableRow key={`pc-${i}`}>
                                      <TableCell>{row.key}</TableCell>
                                      <TableCell align="right">{row.post_count}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Veri yok
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                )}
              </Box>
                </>
              )}
            </TabPanel>

            <TabPanel value={activeTab} index={2}>

            {/* Story / Gönderi listeleri */}

            <>
            {reportConfig.show_stories && reportScope === 'story' && (reportCountedStories.length > 0 ? (

              <Card sx={{ mt: 3 }}>

                <CardContent>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>

                    <Typography variant="h6">

                      Tespit Edilen Paylaşımlar

                    </Typography>

                    <Box display="flex" gap={1} alignItems="center">

                      <Chip 

                        label={`Toplam: ${reportCountedStories.length}`}

                        color="primary"

                        variant="outlined"

                      />

                      <Button

                        variant="contained"

                        color="success"

                        size="small"

                        startIcon={<DownloadIcon />}

                        onClick={handleDownloadAllStories}

                      >

                        Tümünü İndir (ZIP)

                      </Button>

                    </Box>

                  </Box>

                  <Divider sx={{ my: 2 }} />
                  
                  <TableContainer component={Paper} variant="outlined">

                    <Table size="small">

                      <TableHead>

                        <TableRow>

                          <TableCell width="20%">Influencer</TableCell>

                          <TableCell width="10%" align="center">Tip</TableCell>

                          <TableCell width="45%">Tespit Edilen Kriterler</TableCell>

                          <TableCell width="15%" align="center">Tarih</TableCell>

                          <TableCell width="10%" align="center">İşlem</TableCell>

                        </TableRow>

                      </TableHead>

                      <TableBody>

                        {reportCountedStories

                          .slice(storyPage * rowsPerPage, storyPage * rowsPerPage + rowsPerPage)

                          .map((story: any, idx) => (

                            <TableRow key={idx} hover>

                              <TableCell>

                                <Box display="flex" alignItems="center" gap={1.5}>

                                  <Avatar src={story.avatar} alt={story.instagram_username} />

                                  <Box>

                                <Typography variant="body2" sx={{ fontWeight: 500 }}>

                                      <Link

                                        href={`https://www.instagram.com/${story.instagram_username}/`}

                                        target="_blank"

                                        rel="noopener noreferrer"

                                        underline="hover"

                                      >

                                  @{story.instagram_username}

                                      </Link>

                                </Typography>

                                    <Typography variant="caption" color="text.secondary">

                                      {(() => {
                                        const inf = influencers.find((i) => i.id === story.influencer_id);
                                        const name = (inf?.full_name || '').trim();
                                        return name || inf?.instagram_username || '—';
                                      })()}

                                    </Typography>

                                  </Box>

                                </Box>

                              </TableCell>

                              <TableCell align="center">
                                {story.story_type === 'video' ? (
                                  <VideocamIcon fontSize="small" color="secondary" />
                                ) : (
                                  <ImageIcon fontSize="small" color="action" />
                                )}
                              </TableCell>

                              <TableCell>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {Array.isArray(story.detected_keywords) && story.detected_keywords.length > 0 ? (
                                    <>
                                      {story.detected_keywords.slice(0, 5).map((kw: string, i: number) => (
                                        <Chip key={i} label={kw} size="small" color={getKeywordChipColor(kw)} variant="outlined" />
                                      ))}
                                      {story.detected_keywords.length > 5 && (
                                        <Chip label={`+${story.detected_keywords.length - 5}`} size="small" />
                                      )}
                                    </>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>

                              <TableCell align="center">

                                <Typography variant="caption">

                                  {new Date(story.story_timestamp).toLocaleDateString('tr-TR', {

                                    day: '2-digit',

                                    month: 'short',

                                    hour: '2-digit',

                                    minute: '2-digit'

                                  })}

                                </Typography>

                              </TableCell>

                              <TableCell align="center">
                                {(() => {
                                  const canDownload = story.story_type !== 'manual';
                                  const tooltipTitle = canDownload ? 'Story İndir' : 'Bu paylaşım için dosya indirilemez';
                                  return (
                                    <Box display="inline-flex" alignItems="center" gap={0.5}>
                                  <Tooltip title="Instagram’da aç (telefonda uygulama öncelikli)">
                                    <IconButton
                                      size="small"
                                      color="secondary"
                                      type="button"
                                      onClick={() =>
                                        openInstagramStorySmart(
                                          buildInstagramStoryWebUrlPublic(story.instagram_username, story)
                                        )
                                      }
                                    >
                                      <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                    <Tooltip title={tooltipTitle}>
                                      <span>
                                  <IconButton 
                                    size="small" 
                                    color="primary"
                                          disabled={!canDownload}
                                          onClick={() => canDownload && handleDownloadStory(story.id, story)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                      </span>
                                </Tooltip>
                                    </Box>
                                  );
                                })()}
                              </TableCell>

                            </TableRow>

                          ))}
                        
                      </TableBody>

                    </Table>
                    
                    <TablePagination

                      component="div"

                      count={reportCountedStories.length}

                      page={storyPage}

                      onPageChange={handleChangePage}

                      rowsPerPage={rowsPerPage}

                      onRowsPerPageChange={handleChangeRowsPerPage}

                      rowsPerPageOptions={[10, 25, 50, 100]}

                      labelRowsPerPage="Sayfa başına:"

                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}

                    />

                  </TableContainer>

                </CardContent>

              </Card>

            ) : (

              <Alert severity="info" sx={{ mt: 3 }}>
                Henüz tespit edilen story bulunmuyor.
              </Alert>

            ))}

            {reportConfig.show_posts && reportScope === 'post' && (
              <Box sx={{ mt: 3 }}>
                {enrichedPostsWithInfluencer.length > 0 ? (
                  <Card>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={2}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Tespit edilen gönderiler ve Reels
                          </Typography>
                          <Chip
                            label={`Toplam: ${enrichedPostsWithInfluencer.length}`}
                            color="secondary"
                            variant="outlined"
                          />
                        </Box>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadAllTrackedPosts}
                        >
                          Tüm Gönderileri İndir (ZIP)
                        </Button>
                      </Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width="72px" align="center">
                                Önizleme
                              </TableCell>
                              <TableCell width="22%">Influencer</TableCell>
                              <TableCell align="center">Tip</TableCell>
                              <TableCell>Kriterler</TableCell>
                              <TableCell>Tarih</TableCell>
                              <TableCell align="right">İşlem</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {enrichedPostsWithInfluencer
                              .slice(postPage * postRowsPerPage, postPage * postRowsPerPage + postRowsPerPage)
                              .map((post: any) => {
                                const canDl = (post._localPathCount || 0) > 0;
                                return (
                                  <TableRow key={post.id} hover>
                                    <TableCell align="center">
                                      <Box
                                        component="img"
                                        src={post.thumbnail_url || ''}
                                        alt=""
                                        sx={{
                                          width: 56,
                                          height: 56,
                                          objectFit: 'cover',
                                          borderRadius: 1,
                                          bgcolor: 'action.hover',
                                          display: post.thumbnail_url ? 'block' : 'none'
                                        }}
                                      />
                                      {!post.thumbnail_url && (
                                        <PhotoIcon fontSize="small" color="disabled" />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" alignItems="center" gap={1.5}>
                                        <Avatar src={post._avatar} alt={post.instagram_username} />
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            <Link
                                              href={`https://www.instagram.com/${post.instagram_username}/`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              underline="hover"
                                            >
                                              @{post.instagram_username}
                                            </Link>
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {post._fullName || '—'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center">
                                        <Chip
                                          size="small"
                                          label={post.is_reel ? 'Reels' : 'Gönderi'}
                                          color={post.is_reel ? 'secondary' : 'default'}
                                        />
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {(post.keywordList || []).slice(0, 8).map((kw: string, i: number) => (
                                          <Chip key={i} label={kw} size="small" variant="outlined" />
                                        ))}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Typography variant="caption">
                                        {post.posted_at
                                          ? new Date(post.posted_at).toLocaleString('tr-TR', {
                                              day: '2-digit',
                                              month: 'short',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })
                                          : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Box display="inline-flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                                        <Button
                                          size="small"
                                          component={Link}
                                          href={post.permalink || '#'}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          disabled={!post.permalink}
                                        >
                                          Instagram&apos;da aç
                                        </Button>
                                        {canDl && (
                                          <Tooltip title="İndir">
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => handleDownloadPost(post.id, post)}
                                            >
                                              <DownloadIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={enrichedPostsWithInfluencer.length}
                          page={postPage}
                          onPageChange={handleChangePostPage}
                          rowsPerPage={postRowsPerPage}
                          onRowsPerPageChange={handleChangePostRowsPerPage}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                          labelRowsPerPage="Sayfa başına:"
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                        />
                      </TableContainer>
                    </CardContent>
                  </Card>
                ) : (
                  <Alert severity="info">Henüz tespit edilen gönderi yok.</Alert>
                )}
              </Box>
            )}
            </>
            </TabPanel>

            {/* Footer */}

            <Box sx={{ mt: 4, py: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>

              <Typography variant="caption" color="text.secondary">

                Bu rapor Influencer Story Tracker sistemi tarafından oluşturulmuştur

              </Typography>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>

                Rapor Tarihi: {new Date().toLocaleString('tr-TR')}

              </Typography>

            </Box>

          </>

        )}

      </Box>



      {/* Snackbar */}

      {/* The Snackbar component was removed as per the edit hint. */}



      {/* ZIP İndirme Progress Dialog */}

      <Dialog

        open={zipDownloadDialog}

        maxWidth="sm"

        fullWidth

        disableEscapeKeyDown

      >

        <DialogTitle>

          <Box display="flex" alignItems="center" gap={1}>

            <DownloadIcon color="primary" />

            <Typography variant="h6">
              {zipModalContext === 'posts' ? 'Gönderiler İndiriliyor' : "Story'ler İndiriliyor"}
            </Typography>

          </Box>

        </DialogTitle>

        <DialogContent>

          <Box sx={{ py: 3 }}>

            <Box display="flex" alignItems="center" justifyContent="center" mb={2}>

              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>

                {zipProgress.percent}%

              </Typography>

            </Box>

            <LinearProgress

              variant="determinate"

              value={zipProgress.percent}

              sx={{

                height: 10,

                borderRadius: 5,

                mb: 2,

                backgroundColor: 'rgba(0,0,0,0.1)',

                '& .MuiLinearProgress-bar': {

                  borderRadius: 5,

                  background: zipProgress.percent === 100

                    ? 'linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)'

                    : 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)'

                }

              }}

            />

            <Box

              sx={{

                p: 2,

                bgcolor: 'rgba(33, 150, 243, 0.05)',

                borderRadius: 2,

                border: '1px solid rgba(33, 150, 243, 0.2)',

                textAlign: 'center'

              }}

            >

              <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>

                {zipProgress.step}

              </Typography>

            </Box>

            {zipProgress.percent === 100 && (

              <Box display="flex" justifyContent="center" mt={2}>

                <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>

                  ZIP dosyası başarıyla indirildi! 🎉

                </Typography>

              </Box>

            )}

            {campaign && (

              <Box mt={3} display="flex" justifyContent="space-between" sx={{ opacity: 0.7 }}>

                <Typography variant="caption">
                  {zipModalContext === 'posts'
                    ? `Toplam gönderi: ${enrichedPostsWithInfluencer.length}`
                    : `Toplam story: ${reportCountedStories.length}`}
                </Typography>

                <Typography variant="caption">

                  Kampanya: {campaign.title}

                </Typography>

              </Box>

            )}

          </Box>

        </DialogContent>

      </Dialog>

    </Box>

  );

};



export default PublicReport;
